import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  HostBinding,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, ValidationErrors, Validator } from '@angular/forms';
import { BACKEND_DATE_FORMAT, DATE_TIME_FORMAT } from '@app/app.constants';
import { ReactiveComponent } from '@app/shared/components/reactive-component';
import { DateRange } from '@app/shared/models/date-range';
import { DateUtils } from '@app/utils/date';
import { portalError } from '@app/utils/error/portal-error';
import { ngValidatorAccessor } from '@app/utils/ng-validator-accessor';
import { ngValueAccessor } from '@app/utils/ng-value-accessor';
import { isBlank } from '@app/utils/string';
import { NgbDate, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { set } from 'date-fns';

@Component({
  selector: 'portal-date-picker',
  templateUrl: './date-picker.component.html',
  providers: [ngValueAccessor(DatePickerComponent), ngValidatorAccessor(DatePickerComponent)],
  host: {
    class: 'date-picker',
  },
  standalone: false,
})
export class DatePickerComponent
  extends ReactiveComponent
  implements ControlValueAccessor, OnInit, Validator
{
  clearable = input<boolean>(false);
  defaultTime = input<string>();
  id = input<string>();
  name = input<string>();
  maxDate = input<Date>();
  minDate = input<Date>();
  ngClass = input<string | string[] | Set<string> | Record<string, any> | null | undefined>();
  placeholder = input<string>('common.datePicker.placeholder');
  showValidationErrors = input<boolean>(false);
  timeRequired = input<boolean>(false);
  writeWithFormat = input<'date' | 'date-time' | string>();

  @HostBinding('class.date-picker--with-time')
  withTime = input<boolean>(false);

  dateRangeChanged = output<DateRange>();

  datepicker = viewChild(NgbInputDatepicker);
  // We want to ignore time errors when checking if the date is invalid
  dateInvalid = computed(() => this.invalid() && this.dateIsInvalid());
  internalDate = signal<Date | null>(null);
  internalNgbDate = computed(() =>
    this.internalDate() != null ? DateUtils.parseToNgbDate(this.internalDate()) : null
  );
  internalTime = signal<string | 'invalid' | null>(null);
  maximumDate = computed(() => (this.maxDate() != null ? DateUtils.parseToNgbDate(this.maxDate()) : null));
  minimumDate = computed(() => (this.minDate() != null ? DateUtils.parseToNgbDate(this.minDate()) : null));
  startDate = signal<NgbDate>(DateUtils.parseToNgbDate(new Date()));
  timeInputRequired = computed(() => this.timeRequired() && this.defaultTime() == null);

  /**
   * The time shown in the "time" input field. As it is possible
   * to use a default time (shown in the placeholder of the input),
   * we use another variable to keep track of the real value.
   */
  timeUi = signal<string>(null);

  constructor(private datePipe: DatePipe) {
    super();

    effect(() => {
      this.internalTime.set(this.timeUi());
    });
  }

  ngOnInit() {
    this.initializeControl();

    if (this.defaultTime() != null && DateUtils.isValidTime(this.defaultTime()) === false) {
      throw portalError({
        cause: new Error(
          `Given defaultTime is invalid (valid time format: 'HH:mm'). defaultTime=${this.defaultTime()}`
        ),
        id: 'date-picker:invalid-time',
      });
    }
  }

  emit(value: Date | null) {
    if (value == null) {
      this.onChange(null);
    } else {
      let format = this.writeWithFormat();

      if (format != null) {
        if (format === 'date') {
          format = BACKEND_DATE_FORMAT;
        } else if (format === 'date-time') {
          format = DATE_TIME_FORMAT;
        }

        this.onChange(DateUtils.format(value, format));
      } else {
        this.onChange(value);
      }
    }

    this.control.markAsDirty();
    this.control.markAsTouched();
  }

  markDisabled = () => {
    return this.disabled;
  };

  onClear(): void {
    this.clear();
    this.emit(this.internalDate());
  }

  onDateSelection(date: NgbDate) {
    this.internalDate.update((value) => {
      return set(value ?? new Date(), {
        year: date.year,
        month: date.month - 1,
        date: date.day,
      });
    });

    this.onTimeInput(this.timeUi());
    this.emit(this.internalDate());
  }

  onTimeInput(rawTime: string): void {
    this.timeUi.set(rawTime);

    if (DateUtils.isValidTime(rawTime)) {
      const [hours, minutes] = DateUtils.getTime(rawTime);

      this.internalTime.set(rawTime);
      this.internalDate.update((value) => {
        return set(value ?? new Date(), {
          hours: Number(hours),
          milliseconds: 0,
          minutes: Number(minutes),
          seconds: 0,
        });
      });
    } else if (isBlank(rawTime)) {
      if (this.defaultTime() != null) {
        const [hours, minutes] = DateUtils.getTime(this.defaultTime());

        this.internalTime.set(this.defaultTime());
        this.internalDate.update((value) => {
          return set(value ?? new Date(), {
            hours: Number(hours),
            milliseconds: 0,
            minutes: Number(minutes),
            seconds: 0,
          });
        });
      } else {
        this.internalTime.set(null);
      }
    } else {
      this.internalTime.set('invalid');
    }

    this.onValidatorChanged();
    this.emit(this.internalDate());
  }

  validate(): ValidationErrors | null {
    if ((this.timeRequired() && this.internalTime() == null) || this.internalTime() === 'invalid') {
      return {
        invalidTime: {
          key: 'common.datePicker.invalidTime',
        },
      };
    }

    return null;
  }

  writeValue(obj: any): void {
    if (isBlank(obj)) {
      this.clear();
    } else {
      const date = DateUtils.parse(obj);
      const time = this.datePipe.transform(date, 'HH:mm');

      this.internalDate.set(date);
      this.internalTime.set(time);
      this.timeUi.set(time);
    }
  }

  private clear(): void {
    this.internalTime.set('');
    this.internalDate.set(null);
    this.timeUi.set('');
  }

  private dateIsInvalid(): boolean {
    return Object.keys(this.control.errors).some((key) => key !== 'invalidTime');
  }
}
